/**
 * constants.ts
 * All magic numbers live here. Game balance flows from this file.
 * Seeded from the Future Beacon technical briefing (§11).
 */

// ── Map ────────────────────────────────────────────────────────────────
export const TILE_SIZE = 64; // pixels (logical tile size; gameplay grid unit)
export const MAP_WIDTH = 192; // tiles
export const MAP_HEIGHT = 144; // tiles

// ── Isometric projection (2.5D) ────────────────────────────────────────
// Tiles are drawn as 2:1 diamonds. Elevation lifts a tile's top face and
// draws cliff faces down to the ground plane, giving Warcraft-II-style depth.
export const ISO_TILE_WIDTH = 64; // diamond width in px
export const ISO_TILE_HEIGHT = 32; // diamond height in px (2:1)
export const ELEVATION_STEP = 16; // px of vertical lift per height level
export const GROUND_LEVELS = 2; // default land height; water sits below at 0

// ── Resources ──────────────────────────────────────────────────────────
export const STARTING_REPUTATION  = 70;   // just below venue claim cost of 80 REP
export const STARTING_FANBASE     = 50;
export const STARTING_SALES       = 200;
export const STARTING_GEAR        = 0;
export const FANBASE_STARTING_CAP = 100; // initial audience cap
export const REP_MAX              = 9999;
export const SALES_MAX            = 9999;
export const GEAR_MAX             = 999;

// ── Movement-based reputation ─────────────────────────────────────────
// Band members earn REP_PER_TILE_STEP for every tile they step onto.
// This makes active exploration the primary early-game REP source.
export const REP_PER_TILE_STEP = 1;

// ── Scene progression gates ───────────────────────────────────────────
// These are earned-progress thresholds, not spendable balances.  Spending REP
// or SALES should not relock a tier once the scene has genuinely grown.
export const SCENE_BUZZ_REP_REQUIRED       = 140;
export const SCENE_BUZZ_FAN_REQUIRED       = 80;
export const SCENE_BREAKOUT_REP_REQUIRED   = 300;
export const SCENE_BREAKOUT_FAN_REQUIRED   = 140;
export const SCENE_LEGEND_REP_REQUIRED     = 600;
export const SCENE_LEGEND_FAN_REQUIRED     = 220;
export const EXPANSION_BUILD_FAN_REQUIRED  = 75;

// ── Passive income (per second tick) ──────────────────────────────────
// ADMIN: resRepBaseIncome | resSalesBaseIncome | resSalesFanMult
export const RES_REP_BASE_INCOME   = 0.5;  // Reputation/s trickle — movement is the main early source
export const RES_SALES_BASE_INCOME = 2;    // Sales/s baseline
export const RES_SALES_FAN_MULT    = 0.05; // +Sales/s per Fanbase point

// ── Enemy kill rewards ─────────────────────────────────────────────────
// ADMIN: killRepSwarm | killSalesSwarm | killRepMogul | killSalesMogul
export const KILL_REP_SWARM   = 10;
export const KILL_SALES_SWARM = 5;
export const KILL_REP_MOGUL   = 25;
export const KILL_SALES_MOGUL = 15;

// ── Venue income bonuses (per second once claimed, Stage 8) ───────────
// BuildSystem calls ResourceSystem.addVenueBonus() when a venue is booked.
// ADMIN: venueRepDirtyDuck | venueSalesDirtyDuck | venueSalesRecordShop | etc.
export const VENUE_REP_DIRTY_DUCK    = 3;  // +Rep/s   — pub crowd love
export const VENUE_SALES_DIRTY_DUCK  = 1;  // +Sales/s — bar merch
export const VENUE_SALES_RECORD_SHOP = 4;  // +Sales/s — direct record sales
export const VENUE_FAN_HALL          = 2;  // +Fan/s   — community events
export const VENUE_REP_INDIE         = 2;  // +Rep/s   — prestige gig
export const VENUE_SALES_INDIE       = 2;  // +Sales/s — indie ticket sales

// ── Outer venue income bonuses (4 new discovered venues) ──────────────
// ADMIN: venueRepRooftopStage | venueFanRooftopStage | venueSalesArtsCentre |
//        venueFanArtsCentre | venueRepUnderground | venueSalesUnderground |
//        venueRepTownHall | venueFanTownHall
export const VENUE_REP_ROOFTOP_STAGE  = 1;  // +Rep/s   — trendy outdoor gig
export const VENUE_FAN_ROOFTOP_STAGE  = 2;  // +Fan/s   — draws the crowd
export const VENUE_SALES_ARTS_CENTRE  = 3;  // +Sales/s — tickets & merch
export const VENUE_FAN_ARTS_CENTRE    = 2;  // +Fan/s   — arts audience
export const VENUE_REP_UNDERGROUND    = 2;  // +Rep/s   — cred-building basement
export const VENUE_SALES_UNDERGROUND  = 2;  // +Sales/s — door money
export const VENUE_REP_TOWN_HALL      = 2;  // +Rep/s   — civic prestige
export const VENUE_FAN_TOWN_HALL      = 3;  // +Fan/s   — biggest audience

// ── Stage 8: Venue booking costs ─────────────────────────────────────
export const VENUE_CLAIM_COST_REP    = 80;  // REP spent to begin booking
export const VENUE_CLAIM_COST_SALES  = 40;  // SALES spent to begin booking

// ── Stage 8: Venue build / defend mechanics ──────────────────────────
export const VENUE_CLAIM_TIME        = 8.0; // seconds to complete a booking
export const VENUE_CLAIM_RANGE       = 2.0; // tiles — unit must be within access tile
export const VENUE_HP_MAX            = 100;
export const VENUE_ENEMY_DPS         = 8;   // HP/s damage from enemy presence
export const VENUE_REGEN_RATE        = 0.04; // fraction of maxHp healed/s when safe

// ── Beacon ─────────────────────────────────────────────────────────────
export const BEACON_STARTING_SIGNAL = 75;
export const BEACON_MAX_SIGNAL = 100;
export const SIGNAL_FRIENDLY_BONUS = 0.5; // per nearby unit per tick
export const SIGNAL_ENEMY_DRAIN = 0.55; // per nearby enemy per tick
export const SONG_DROP_THRESHOLD = 75; // signal must exceed this
export const SONG_DROP_SUSTAIN_TIME = 60; // seconds above threshold

// ── Rhythm ─────────────────────────────────────────────────────────────
// ADMIN: tempoDefault | tempoDecayRate | tempoEnemyHit
export const TEMPO_DEFAULT    = 0.5;
export const TEMPO_DECAY_RATE = 0.003; // per frame at 60fps (×60 = 0.18/s); ~5s to recover
export const TEMPO_ENEMY_HIT  = 0.2;  // tempo reduction per enemy hit on Chris

// ── Player unit combat ─────────────────────────────────────────────────
export const UNIT_ATTACK_RATE   = 1.0; // attacks per second
export const UNIT_SIGHT_RADIUS  = 5;   // tiles — detection radius
export const UNIT_ATTACK_RADIUS = 1.5; // tiles — melee range
// ADMIN: unitDamage
export const UNIT_DAMAGE        = 20;  // damage per hit

// ── Enemy stats ────────────────────────────────────────────────────────
// ADMIN: enemyHpMogul | enemyHpSwarm | enemyHpBadManager | enemyHpTribute | enemyHpMusicPress | enemyHpPirate
export const ENEMY_HP_MOGUL          = 80;  // Streaming Mogul — tough signal pressure
export const ENEMY_HP_SWARM          = 30;  // GenZ Swarm — fragile raider
export const ENEMY_HP_BAD_MANAGER    = 55;  // Bad Manager — infiltrator
export const ENEMY_HP_TRIBUTE        = 160; // Corporate Tribute Act — slow tank
export const ENEMY_HP_MUSIC_PRESS    = 45;  // Music Press — neutral timed threat
export const ENEMY_HP_PIRATE         = 46;  // File Sharing Pirate — fragile ranged harasser
export const ENEMY_HP_RIVAL_BUILDER  = 70;  // Rival Venue Builder — non-combat map pressure
// ADMIN: enemyDamageMogul | enemyDamageSwarm | enemyDamageBadManager | enemyDamageTribute | enemyDamageMusicPress | enemyDamagePirate
export const ENEMY_DAMAGE_MOGUL      = 5;   // weak contact damage plus signal pressure
export const ENEMY_DAMAGE_SWARM      = 6;
export const ENEMY_DAMAGE_BAD_MANAGER = 5;
export const ENEMY_DAMAGE_TRIBUTE    = 18;
export const ENEMY_DAMAGE_MUSIC_PRESS = 4;
export const ENEMY_DAMAGE_PIRATE     = 8;
export const ENEMY_DAMAGE_RIVAL_BUILDER = 0;
// ADMIN: enemyMoveSpeedMogul | enemyMoveSpeedSwarm | enemyMoveSpeedBadManager | enemyMoveSpeedTribute | enemyMoveSpeedMusicPress | enemyMoveSpeedPirate
export const ENEMY_MOVE_SPEED_MOGUL  = 1.5; // slow and tanky
export const ENEMY_MOVE_SPEED_SWARM  = 3.5; // fast and fragile
export const ENEMY_MOVE_SPEED_BAD_MANAGER = 3.0;
export const ENEMY_MOVE_SPEED_TRIBUTE = 1.2;
export const ENEMY_MOVE_SPEED_MUSIC_PRESS = 2.2;
export const ENEMY_MOVE_SPEED_PIRATE = 2.7;
export const ENEMY_MOVE_SPEED_RIVAL_BUILDER = 2.0;
// ADMIN: enemyAttackRate | enemySightRadius | enemyAttackRadius
export const ENEMY_ATTACK_RATE       = 1.0;
export const ENEMY_SIGHT_RADIUS      = 4;   // tiles
export const ENEMY_ATTACK_RADIUS     = 1.5; // tiles (melee)
export const MUSIC_PRESS_HOSTILE_TIME = 60; // seconds before neutral press turns hostile
export const MUSIC_PRESS_REP_REWARD = 60;
export const MUSIC_PRESS_FAN_REWARD = 15;
export const MUSIC_PRESS_BAD_REVIEW_DURATION = 30; // s of reduced Pub reputation income
export const MUSIC_PRESS_BAD_REVIEW_MULT = 0.35;   // Pubs retain 35% of REP income

// ── Rival Venue footholds ──────────────────────────────────────────────
export const RIVAL_VENUE_HP = 120;
export const RIVAL_VENUE_BUILD_TIME = 24; // seconds to construct once builder reaches site
export const RIVAL_VENUE_SPAWN_INTERVAL = 34; // seconds between completed-venue spawn pulses
export const RIVAL_VENUE_MAX_ACTIVE = 3;
export const RIVAL_VENUE_MIN_BEACON_DISTANCE = 13; // keep early footholds away from the centre
export const RIVAL_VENUE_SITE_RADIUS_MIN = 15;
export const RIVAL_VENUE_SITE_RADIUS_MAX = 34;
export const RIVAL_VENUE_SPAWN_RADIUS = 2;
export const RIVAL_VENUE_LEVEL_2_TIME = 70; // seconds ignored before the rival crowd grows
export const RIVAL_VENUE_LEVEL_3_TIME = 145; // seconds ignored before it becomes a serious scene hub
export const RIVAL_VENUE_LEVEL_HP_BONUS = 35; // extra max HP per escalation level after level 1
export const RIVAL_VENUE_LEVEL_SPAWN_ACCEL = 0.82; // spawn interval multiplier per level after level 1

// ── Corporate HQ hidden objective ──────────────────────────────────────
export const CORPORATE_HQ_HP = 320;
export const CORPORATE_HQ_SPAWN_INTERVAL = 38; // seconds between factory pressure groups
export const CORPORATE_HQ_DISCOVERY_RADIUS = 8; // tiles from HQ access that triggers reveal toast
export const CORPORATE_HQ_HIDDEN_ACTIVATION_WAVE = 4; // undiscovered HQ stays silent until late game
export const CORPORATE_HQ_EDGE_SPAWN_EXCLUSION_RADIUS = 18; // keep generic edge waves off the secret island
export const CORPORATE_HQ_CAMP_RADIUS = 7; // tiles around the access road treated as a siege camp
export const CORPORATE_HQ_SECURITY_INTERVAL = 12; // seconds before HQ dispatches anti-camping security
export const CORPORATE_HQ_HINT_PULSE_INTERVAL = 75; // seconds between late-game hidden HQ signal hints
export const CORPORATE_HQ_HINT_PULSE_WAVE = 5; // first wave where the hidden HQ starts leaking signal

// ── Engine ─────────────────────────────────────────────────────────────
export const TARGET_FPS = 60;
export const FOG_UPDATE_INTERVAL = 500; // ms — FogOfWar refresh cadence
export const SAVE_AUTOSAVE_INTERVAL = 60; // seconds
export const SAVE_KEY = 'future-beacon-save';

// ── Camera ─────────────────────────────────────────────────────────────
export const CAMERA_PAN_SPEED = 750; // screen px per second (keyboard / edge scroll)
export const CAMERA_EDGE_SCROLL_MARGIN = 24; // px from viewport edge that triggers edge-scroll
export const CAMERA_ZOOM_MAX = 3.0;
export const CAMERA_ZOOM_STEP = 1.12; // zoom multiplier per wheel notch

// ── Unit HP — per class (tunable via balance table) ───────────────────
// ADMIN: unitHpProtector | unitHpPromoter | unitHpEngine | unitHpSustain | unitHpMystic | unitHpSongwriter
export const UNIT_HP_PROTECTOR  = 150; // Guitar Gang — tanky frontline
export const UNIT_HP_PROMOTER   = 75;  // Vocal Mob — fast but fragile
export const UNIT_HP_ENGINE     = 100; // Rhythm Crew — medium
export const UNIT_HP_SUSTAIN    = 80;  // Bass Line — logistics, not combat
export const UNIT_HP_MYSTIC     = 60;  // Keyboard Prophets — very fragile
export const UNIT_HP_SONGWRITER = 110; // Song Catchers — medium-high; must survive to broadcast

// ── Unit move speed (tiles/s, multiplied by Tempo modifier) ───────────
// ADMIN: unitMoveSpeed | unitMoveSpeedPromoter | unitMoveSpeedMystic | unitMoveSpeedSongwriter
export const UNIT_MOVE_SPEED            = 3.0; // default for most classes
export const UNIT_MOVE_SPEED_PROMOTER   = 4.5; // Ali's Vocal Mob is fastest
export const UNIT_MOVE_SPEED_MYSTIC     = 2.0; // Keyboard Prophets are slow
export const UNIT_MOVE_SPEED_SONGWRITER = 3.5; // Song Catchers — mobile, needs to reposition

// ── Fan units (spawned when a venue is claimed) ────────────────────────
export const FAN_SPAWN_COUNT   = 2;   // fans spawned per venue claim
export const FAN_HP            = 30;  // fragile — they're just fans
export const FAN_MOVE_SPEED    = 3.5; // tiles/s — enthusiastic
export const FAN_SIGHT_RADIUS  = 2;   // tiles — small fog reveal; no combat targeting
export const FAN_DAMAGE        = 2;   // tiny but real — fans can help finish enemies
export const FAN_ATTACK_RATE   = 0.55;
export const FAN_ATTACK_RADIUS = 1.5;

// ── Band member down/revive loop ───────────────────────────────────────
export const BAND_REVIVE_RANGE      = 1.5;  // tiles — any active friendly unit must stand nearby
export const BAND_REVIVE_TIME       = 5.0;  // seconds of nearby help to revive
export const BAND_REVIVE_HP_RATIO   = 0.35; // revived HP as a fraction of maxHp

// ── Fan raid penalties ─────────────────────────────────────────────────
export const FAN_KILL_STEAL_SALES   = 18; // enemies steal merch/ticket cash when fans are killed
export const FAN_KILL_STEAL_GEAR    = 4;  // gear dropped by mining/venue-running fans
export const FAN_KILL_STEAL_FANBASE = 1;  // morale hit from losing the crowd
export const FAN_KILL_REP_LOSS       = 5;  // public confidence hit when fans are killed
export const VENUE_LOST_REP_LOSS     = 20; // venue loss damages the band's reputation

// ── Jeopardy: fan decay and venue engagement ───────────────────────────
export const FAN_DECAY_PER_SEC              = 0.4;   // passive crowd drift — venues must offset this
export const SALES_UPKEEP_PER_SEC          = 2.5;   // band running costs drain sales every second
export const VENUE_ENGAGEMENT_DECAY_RATE   = 0.012; // engagement lost/s when no player unit nearby (~83s to zero)
export const VENUE_ENGAGEMENT_RECHARGE_RATE = 0.10; // engagement gained/s when a player unit is near (~10s to full)
export const VENUE_ENGAGEMENT_WARNING      = 0.30;  // toast fires when engagement drops below this

// ── Unit rendering ────────────────────────────────────────────────────
export const UNIT_RADIUS_RECRUIT = 8;  // px — base unit circle radius
export const UNIT_RADIUS_HERO    = 12; // px — band leader circle radius
export const UNIT_RADIUS_FAN     = 5;  // px — small crowd dot
export const UNIT_RADIUS_MOGUL   = 14; // px — slow tanky Streaming Mogul
export const UNIT_RADIUS_SWARM   = 7;  // px — fast fragile GenZ Swarm
export const UNIT_RADIUS_BAD_MANAGER = 9;
export const UNIT_RADIUS_TRIBUTE = 13;
export const UNIT_RADIUS_MUSIC_PRESS = 10;
export const UNIT_RADIUS_PIRATE = 10;
export const UNIT_HIT_RADIUS     = 28; // px — world-space click selection radius
export const ATTACK_PULSE_DURATION = 0.22; // seconds — orange swing readability flash

// ── Gear mining (fan resource loop) ───────────────────────────────────
export const GEAR_MINE_RATE          = 1.0; // GEAR per second per fan on a depot
export const GEAR_MINE_RANGE         = 1.5; // tiles — fan must be within this to mine
export const GEAR_FAN_CLAIM_BOOST    = 0.3; // claim speed bonus fraction per fan present at venue
export const GEAR_FAN_CLAIM_BOOST_MAX = 3;  // max fans that contribute to the boost

// ── Fan Workshop — build sites unlocked by depositing GEAR ────────────
// Fans mine GEAR at depots, then carry it to nearby workshop sites.
// When a site receives VENUE_BUILD_GEAR_COST GEAR it unlocks as a new
// unclaimed venue (free to claim — fans already did the legwork).
export const VENUE_BUILD_GEAR_COST  = 30;  // GEAR required to complete one workshop
export const VENUE_BUILD_GEAR_RANGE = 2.0; // tiles — fan must be within this to deposit
export const VENUE_BUILD_GEAR_RATE  = 2.0; // GEAR/s deposited per fan working the site

// ── Beacon signal thresholds (HUD / behaviour bands, §9.5) ─────────────
export const SIGNAL_BAND_PROPHECY = 75; // above: prophecy warnings fire
export const SIGNAL_BAND_NORMAL = 50; // 50–75: normal operation
export const SIGNAL_BAND_REDUCED = 25; // 25–50: reputation income -25%
// below 25: no song drops, sales drain begins, HUD warning state
export const BEACON_NEAR_RADIUS          = 7;  // tiles from beacon centre counted as nearby
export const SIGNAL_SALES_DRAIN_CRITICAL = 5;  // sales/s drained while in critical band

// ── Beacon ownership ───────────────────────────────────────────────────
// One side must hold the Future Beacon plaza uncontested for this many
// continuous seconds to flip control.
export const BEACON_CAPTURE_TIME = 18; // seconds of sustained control to capture/recapture

// ── Song Drop effects (Stage 10) ───────────────────────────────────────
export const SONG_DROP_REP_BONUS      = 50;  // reputation granted on drop
export const SONG_DROP_SALES_BONUS    = 30;  // sales granted on drop
export const SONG_DROP_GEAR_BONUS     = 20;  // gear granted on drop
export const SONG_DROP_ANTHEM_SECS    = 10;  // seconds of anthem speed buff on friendlies
export const SONG_DROP_ANTHEM_MULT    = 1.5; // movement speed multiplier during anthem buff
export const SONG_DROP_STUN_SECS      = 3;   // seconds enemies near beacon are stunned

// ── Wave escalation (Stage 10) ────────────────────────────────────────
export const WAVE_INFINITE_INTERVAL   = 70;  // seconds between escalating waves (post onboarding)

// ── Enemy objectives ──────────────────────────────────────────────────
export const BADDIE_BEACON_BUILD_TIME = 45;  // seconds before the rival beacon comes online
export const BADDIE_BEACON_HP         = 180; // under-construction or active HP
export const BADDIE_BEACON_JAMMING    = 1.25; // signal drain per second while active
export const GOOD_BEACON_PULSE_MIN    = 45;  // seconds — earliest future-song heal pulse
export const GOOD_BEACON_PULSE_MAX    = 90;  // seconds — latest future-song heal pulse
export const GOOD_BEACON_HEAL_RATIO   = 0.5; // fraction of max HP restored to good forces
export const BADDIE_BEACON_PULSE_TIME = 60;  // seconds between worker-steal pulses
export const CAPTURED_BEACON_PULSE_TIME = 45; // seconds between red Future Beacon pulses
export const CAPTURED_BEACON_DAMAGE_RATIO = 0.10; // max HP removed from each goodie per red pulse
export const BADDIE_BEACON_ONLINE_GUARDS = 2; // defenders spawned when broadcast starts
export const BADDIE_BEACON_PULSE_GUARDS  = 1; // fallback defenders when no fans are stolen

// ── Fog of War ────────────────────────────────────────────────────────
// ADMIN: fogExploredAlpha | fogUnexploredAlpha
export const FOG_EXPLORED_ALPHA   = 0.35; // opacity of dark veil over visited tiles (0 = clear, 1 = black)
export const FOG_UNEXPLORED_ALPHA = 1.0;  // opacity over never-visited tiles

// ── Jim's spell system (Stage 15) ────────────────────────────────────
// Six spells castable by Jim (mystic) or Keyboard Prophet recruits.
// Losing Jim triggers a 60-second lockout on all spells.
export const SPELL_LOCKOUT_TIME        = 60;   // seconds of lockout after Jim goes down
// Signal Boost
export const SPELL_SIGNAL_BOOST_DUR    = 30;   // seconds signal output is doubled
// Temporal Echo
export const SPELL_TEMPORAL_ECHO_DUR   = 15;   // seconds enemies are slowed
export const SPELL_TEMPORAL_ECHO_RAD   = 9;    // tiles radius from caster
export const SPELL_TEMPORAL_ECHO_SLOW  = 0.5;  // movement speed multiplier when slowed
// Crowd Confusion
export const SPELL_CROWD_CONFUSE_DUR   = 10;   // seconds target fights own side
// Feedback Loop
export const SPELL_FEEDBACK_RAD        = 7;    // tiles radius from caster
export const SPELL_FEEDBACK_DMG        = 55;   // base damage per enemy
export const SPELL_FEEDBACK_MOGUL_MULT = 2.5;  // extra damage multiplier vs StreamingMoguls
// Bassline Summon
export const SPELL_BASSLINE_SALES      = 150;  // emergency Sales added to resources
// Cooldowns (seconds)
export const SPELL_CD_SIGNAL_BOOST    = 90;
export const SPELL_CD_TEMPORAL_ECHO   = 60;
export const SPELL_CD_CROWD_CONFUSE   = 45;
export const SPELL_CD_FEEDBACK_LOOP   = 120;
export const SPELL_CD_PROPHECY_REVEAL = 180;
export const SPELL_CD_BASSLINE_SUMMON = 180;

// ── Band member active skills ─────────────────────────────────────────
// One skill per class, triggered with Q (or button). Cooldowns in seconds.
export const SKILL_COOLDOWN_PROTECTOR  = 18;   // Power Chord
export const SKILL_COOLDOWN_PROMOTER   = 20;   // Crowd Hype
export const SKILL_COOLDOWN_ENGINE     = 25;   // Tempo Surge
export const SKILL_COOLDOWN_SUSTAIN    = 16;   // Bassline
export const SKILL_COOLDOWN_MYSTIC     = 30;   // Prophecy
export const SKILL_COOLDOWN_SONGWRITER = 22;   // Future Verse
// Effect parameters
export const SKILL_STUN_DURATION        = 2.5;  // Power Chord: enemy stun (seconds)
export const SKILL_STUN_RADIUS          = 3.5;  // Power Chord: area of effect (tiles)
export const SKILL_HYPE_DURATION        = 5.0;  // Crowd Hype: speed buff (seconds)
export const SKILL_HYPE_RADIUS          = 4.0;  // Crowd Hype: area of effect (tiles)
export const SKILL_SPEED_BOOST_MULT     = 1.6;  // Crowd Hype: movement speed multiplier
export const SKILL_SURGE_DURATION       = 4.0;  // Tempo Surge: attack buff (seconds)
export const SKILL_ATTACK_BOOST_MULT    = 1.75; // Tempo Surge: attack speed multiplier
export const SKILL_HEAL_RATIO           = 0.30; // Bassline: heal fraction of maxHp
export const SKILL_HEAL_RADIUS          = 3.5;  // Bassline: area of effect (tiles)
export const SKILL_PROPHECY_RADIUS      = 10;   // Prophecy: fog reveal radius (tiles)
export const SKILL_VERSE_STUN_DURATION  = 1.5;  // Future Verse: enemy stun (shorter than Power Chord)
export const SKILL_VERSE_HEAL_RATIO     = 0.20; // Future Verse: ally heal fraction of maxHp
export const SKILL_VERSE_RADIUS         = 3.5;  // Future Verse: area of effect (tiles)
// Touring / distance-bonus mechanics — reward spreading across the larger map
export const SKILL_TOURING_REP_THRESHOLD = 15;  // tiles from Beacon before ×3 REP bonus applies
export const SKILL_TOURING_REP_MULT      = 3;   // REP-per-step multiplier when touring
export const SKILL_HYPE_FAR_THRESHOLD    = 12;  // Ali: tiles from Beacon to trigger Tour Advance
export const SKILL_HYPE_FAR_REVEAL       = 8;   // Ali: fog reveal radius when touring
export const SKILL_PROPHECY_FAR_THRESHOLD = 15; // Jim: tiles from Beacon to trigger Deep Prophecy
export const SKILL_PROPHECY_FAR_RADIUS   = 20;  // Jim: reveal radius when deep in the field
export const SKILL_BASSLINE_VENUE_RANGE  = 5;   // Alex: tile radius for venue proximity check
export const SKILL_BASSLINE_VENUE_SALES  = 20;  // Alex: Sales injected when near claimed venue
export const SKILL_BASSLINE_VENUE_GEAR   = 10;  // Alex: Gear injected when near claimed venue

// ── Stage 14: Codex enemy AI ──────────────────────────────────────────────
export const CODEX_DECIDE_INTERVAL     = 15;   // s between BattlePlanner ticks
export const BADMANAGER_CORRUPT_DUR    = 8;    // s a player unit stays corrupted
export const BADMANAGER_DETECTION_RADIUS = 4.5; // tiles around Keyboard Prophets
export const BADMANAGER_DETECTED_STUN  = 2.5;  // s manager is exposed when detected
export const MOGUL_AURA_INTERVAL       = 8;    // s between Mogul interference pulses
export const MOGUL_AURA_JAM_DUR        = 4;    // s player units are slowed by the aura
export const MOGUL_AURA_RADIUS         = 3.5;  // tiles radius of the aura
export const MOGUL_SIGNAL_DRAIN        = 0.22; // signal/s per living Mogul anywhere on map
export const MOGUL_SALES_DRAIN         = 0.45; // sales/s per living Mogul anywhere on map
export const MOGUL_GLOBAL_DRAIN_CAP    = 6;    // cap stacked global pressure
export const GENZ_BURST_INTERVAL       = 7;    // s between GenZSwarm speed bursts
export const GENZ_BURST_DUR            = 1.8;  // burst sprint duration (s)
export const GENZ_BURST_SPEED_MULT     = 2.0;  // burst speed multiplier
export const TRIBUTE_RALLY_RADIUS      = 4.5;  // tiles — TributeAct looks for allies
export const TRIBUTE_RALLY_MIN_ALLIES  = 2;    // allies required before TributeAct charges

// ── Player-built buildings (Stage 8) ──────────────────────────────────────────
// Costs
export const BUILD_PUB_COST_REP       = 150;
export const BUILD_PUB_COST_SALES     = 60;
export const BUILD_FLYER_COST_REP     = 60;
export const BUILD_FLYER_COST_SALES   = 30;
export const BUILD_MERCH_COST_REP     = 100;
export const BUILD_MERCH_COST_SALES   = 50;
export const BUILD_RIG_COST_REP       = 200;
export const BUILD_RIG_COST_GEAR      = 2;
// Effects (per second when constructed)
export const BUILD_PUB_REP_INCOME     = 2;
export const BUILD_MERCH_SALES_INCOME = 1.5;
export const BUILD_FLYER_FAN_BONUS    = 10;  // flat cap bonus per post
// Construction time (seconds)
export const BUILD_TIME_PUB           = 8;
export const BUILD_TIME_FLYER         = 5;
export const BUILD_TIME_MERCH         = 6;
export const BUILD_TIME_RIG           = 10;
// Sound Rig attack
export const BUILD_RIG_ATTACK_RADIUS  = 6;
export const BUILD_RIG_DAMAGE         = 8;
export const BUILD_RIG_ATTACK_RATE    = 1.5;

// ── Stage 18: Venue Upgrades & Build Escalation ───────────────────────────────
// Venue upgrade costs (rep, sales) per level transition
export const VENUE_UPGRADE_L2_REP    = 25;   // REP cost to upgrade a claimed venue to Level 2
export const VENUE_UPGRADE_L2_SALES  = 20;   // SALES cost to upgrade to Level 2
export const VENUE_UPGRADE_L3_REP    = 60;   // REP cost to upgrade to Level 3
export const VENUE_UPGRADE_L3_SALES  = 50;   // SALES cost to upgrade to Level 3
// Income multipliers per level
export const VENUE_INCOME_MULT_L2    = 1.6;  // × base income at Level 2
export const VENUE_INCOME_MULT_L3    = 2.6;  // × base income at Level 3
// HP multipliers per level
export const VENUE_HP_MULT_L2        = 1.5;  // × VENUE_HP_MAX at Level 2
export const VENUE_HP_MULT_L3        = 2.5;  // × VENUE_HP_MAX at Level 3
// Build score escalation: enemy pressure only ramps after a long grace period
export const BUILD_SCORE_ESCALATION_DELAY = 8;   // infinite waves before build score affects pressure
export const BUILD_SCORE_ESCALATION_DIV   = 20;  // build score points needed to add 1 extra enemy
export const BUILD_SCORE_ESCALATION_CAP   = 4;   // hard cap on extra enemies from build score
// Nigel (songwriter) proximity bonus to beacon signal generation
export const NIGEL_PROXIMITY_RADIUS  = 12;   // tiles from beacon centre
export const NIGEL_SIGNAL_BONUS      = 1.5;  // extra "unit equivalents" of signal when Nigel is near
// Song Drop enhanced fanbase burst
export const SONG_DROP_FANS_PER_VENUE = 5;   // fans added per claimed venue on Song Drop
